import AbstractAnalyticsInjector from '@/analytics/AbstractAnalyticsInjector'
import zenkyEvents from '@/events'
import {variantDescription} from "@/helpers/products";

export default class YandexMetrika extends AbstractAnalyticsInjector {
  getEvents() {
    return {
      [zenkyEvents.CALLBACK_SUBMITTED]: 'ZENKY_CALLBACK_SUBMITTED',
      [zenkyEvents.FEEDBACK_SUBMITTED]: 'ZENKY_FEEDBACK_SUBMITTED',
      [zenkyEvents.ORDER_SUBMISSION_STARTED]: 'ZENKY_ORDER_SUBMISSION_STARTED',
      [zenkyEvents.PRODUCT_OPENED]: 'ZENKY_PRODUCT_OPENED',
      [zenkyEvents.PRODUCT_ADDED_TO_ORDER]: 'ZENKY_PRODUCT_ADDED_TO_ORDER',
      [zenkyEvents.PRODUCT_REMOVED_FROM_ORDER]: 'ZENKY_PRODUCT_REMOVED_FROM_ORDER',
      [zenkyEvents.ORDER_SUBMITTED]: 'ZENKY_ORDER_SUBMITTED',
    }
  }

  load(id, config, head) {
    return new Promise((resolve, reject) => {
      window.ym = window.ym || function () {
        (window.ym.a = window.ym.a || []).push(arguments)
      };
      window.ym.l = new Date() * 1

      const script = document.createElement('script')
      script.async = true
      script.src = 'https://mc.yandex.ru/metrika/tag.js'
      script.onload = () => resolve()
      script.onerror = err => reject(err)

      head.appendChild(script)
    })
  }

  init(config) {
    if (typeof window.ym === 'undefined' || !window.ym) {
      return false
    }

    window.dataLayer = window.dataLayer || []
    window.ym(this.accountId, 'init', config)

    return true
  }

  handleEvent(event, externalEvent, payload) {
    console.log(`[YandexMetrika] Got event "${event}"`, { externalEvent, payload })

    window.ym(
      this.accountId,
      'reachGoal',
      externalEvent,
      this.generateGoalParams(event, payload)
    )

    this.sendEcommerceData(event, payload)
  }

  sendEcommerceData(event, payload) {
    if (!Array.isArray(window.dataLayer)) {
      window.dataLayer = []
    }

    const actionType = this.getActionType(event)

    if (!actionType) {
      return
    }

    const currencyCode = this.getCurrencyCode(event, payload)
    const products = this.getProducts(event, payload)

    const ecommerce = {
      currencyCode,
      [actionType]: {
        products,
      },
    }

    if (actionType === 'purchase') {
      ecommerce[actionType].actionField = this.getActionField(event, payload)
    }

    window.dataLayer.push({
      ecommerce,
    })
  }

  getCurrencyCode(event, payload) {
    switch (event) {
      case zenkyEvents.PRODUCT_OPENED:
        if (!payload?.product?.variants?.length) {
          return 'RUB'
        }

        return payload.product.variants[0]?.price?.currency?.code || 'RUB'
      case zenkyEvents.PRODUCT_ADDED_TO_ORDER:
      case zenkyEvents.PRODUCT_REMOVED_FROM_ORDER:
        return payload?.variant?.price?.currency?.code || 'RUB'
      case zenkyEvents.ORDER_SUBMISSION_STARTED:
      case zenkyEvents.ORDER_SUBMITTED:
        return payload?.order?.total_price?.currency?.code || 'RUB'
    }

    if (this.app.$store.getters['store/hasCurrency']) {
      return this.app.$store.getters['store/currency']?.code || 'RUB'
    }

    return 'RUB'
  }

  getActionType(event) {
    switch (event) {
      case zenkyEvents.PRODUCT_OPENED:
        return 'detail'
      case zenkyEvents.PRODUCT_ADDED_TO_ORDER:
        return 'add'
      case zenkyEvents.PRODUCT_REMOVED_FROM_ORDER:
        return 'remove'
      case zenkyEvents.ORDER_SUBMITTED:
        return 'purchase'
    }

    return null
  }

  getActionField(event, payload) {
    return {
      id: payload.order.id,
    }
  }

  getProducts(event, payload) {
    switch (event) {
      case zenkyEvents.PRODUCT_OPENED:
        return [ this.getSingleProduct(payload.product) ]
      case zenkyEvents.PRODUCT_ADDED_TO_ORDER:
      case zenkyEvents.PRODUCT_REMOVED_FROM_ORDER:
        return [ this.getSingleProduct(payload.product, payload.variant, payload.quantity) ]
      case zenkyEvents.ORDER_SUBMITTED:
        return payload.variants.map((variant, index) => this.getSingleProduct(variant.product, variant.variant, variant.quantity, index))
    }

    return []
  }

  getSingleProduct(product, variant, quantity, position) {
    if (product === null && variant && variant.id) {
      product = {
        id: variant.product_id,
        name: variantDescription(variant),
        categories: [],
        variants: [variant],
      }
    }

    const categoriesCount = product?.categories?.length || 0
    const variantsCount = product?.variants?.length || 0

    if (!variant && variantsCount > 0) {
      variant = product.variants[0]
    }

    return {
      id: product.id,
      name: product.name,
      category: categoriesCount > 0 ? product.categories[0].name : '',
      position: position || 1,
      price: parseFloat(variant?.price?.trimmed || '0'),
      quantity: quantity || 1,
      variant: variantDescription(variant),
    }
  }

  generateGoalParams(event, payload) {
    switch (event) {
      case zenkyEvents.ORDER_SUBMISSION_STARTED:
      case zenkyEvents.ORDER_SUBMITTED:
        return {
          order_price: parseFloat(payload?.order?.total_price?.value / 100 || '0'),
          currency: payload.order.total_price.currency.code || 'RUB',
        }
    }

    return {}
  }
}
